<script setup lang="ts">
import { z } from 'zod'

const props = withDefaults(defineProps<{
  variant?: 'default' | 'compact'
  initialUsername?: string
  /** Where the widget sits, reported with the `claim_click` goal (guides/28) */
  location?: string
}>(), {
  variant: 'default',
  initialUsername: '',
  location: 'hero'
})

const router = useRouter()
const { trackGoal } = useDatafast()

const inputEl = ref<HTMLInputElement | null>(null)
const username = ref('')
const isChecking = ref(false)
const isAvailable = ref<boolean | null>(null)
const validationError = ref<string | null>(null)
// "Pick a username first" hint after clicking Claim with an empty field
const nudge = ref(false)
// Claim clicked while the availability check was still running
const claimPending = ref(false)
// Available alternatives offered when the typed name is taken
const suggestions = ref<string[]>([])
let suggestionRun = 0

// Animated placeholder names
const exampleNames = ['mia', 'novabeats', 'arlo', 'lenamakes', 'wren', 'kaiplays', 'inkbyjess', 'solfaye']
const currentPlaceholder = ref(exampleNames[0])
let placeholderIndex = 0
let placeholderInterval: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  placeholderInterval = setInterval(() => {
    placeholderIndex = (placeholderIndex + 1) % exampleNames.length
    currentPlaceholder.value = exampleNames[placeholderIndex]
  }, 2500)

  // Pre-fill and check initial username if provided
  if (props.initialUsername) {
    onInput(props.initialUsername)
  }
})

onUnmounted(() => {
  if (placeholderInterval) clearInterval(placeholderInterval)
})

const usernameSchema = z.string()
  .min(3, 'At least 3 characters')
  .max(20, 'Max 20 characters')
  .regex(/^[a-z0-9_-]+$/, 'Only lowercase letters, numbers, hyphens, underscores')

const checkAvailability = useDebounceFn(async (value: string) => {
  const result = usernameSchema.safeParse(value)
  if (!result.success) {
    validationError.value = result.error.issues[0]?.message || 'Invalid username'
    isAvailable.value = null
    isChecking.value = false
    return
  }

  validationError.value = null
  isChecking.value = true

  try {
    const data = await $fetch<{ available: boolean, reason?: string }>('/api/character', {
      query: { username: value, check: 'availability' }
    })

    if (data.reason === 'invalid_format') {
      validationError.value = 'Invalid username format'
      isAvailable.value = false
      return
    }

    if (data.reason === 'reserved') {
      validationError.value = 'This username is not available to claim'
      isAvailable.value = false
      return
    }

    isAvailable.value = data.available
    if (!data.available && data.reason !== 'reserved') findSuggestions(value)
  } catch {
    validationError.value = 'Could not check availability'
    isAvailable.value = null
  } finally {
    isChecking.value = false
    // Claim was clicked mid-check: continue as soon as the answer is in
    if (claimPending.value) {
      claimPending.value = false
      if (isAvailable.value) goToSignup()
      else inputEl.value?.focus()
    }
  }
}, 500)

// A few close variants of a taken name, keeping only the ones that are free
async function findSuggestions(base: string) {
  const run = ++suggestionRun
  const candidates = [`${base}hq`, `its${base}`, `${base}_`, `the${base}`, `${base}x`]
    .filter(c => usernameSchema.safeParse(c).success)
    .slice(0, 4)
  const results = await Promise.all(candidates.map(c =>
    $fetch<{ available: boolean }>('/api/character', { query: { username: c, check: 'availability' } })
      .then(r => (r.available ? c : null))
      .catch(() => null)
  ))
  if (run === suggestionRun && username.value === base) suggestions.value = results.filter((c): c is string => !!c).slice(0, 3)
}

function onInput(value: string) {
  const cleaned = value.toLowerCase().replace(/[^a-z0-9_-]/g, '')
  username.value = cleaned
  nudge.value = false
  suggestions.value = []
  suggestionRun++

  if (cleaned.length === 0) {
    isAvailable.value = null
    validationError.value = null
    isChecking.value = false
    return
  }

  if (cleaned.length < 3) {
    validationError.value = 'At least 3 characters'
    isAvailable.value = null
    isChecking.value = false
    return
  }

  isChecking.value = true
  isAvailable.value = null
  validationError.value = null
  checkAvailability(cleaned)
}

function useSuggestion(name: string) {
  onInput(name)
  inputEl.value?.focus()
}

function goToSignup() {
  router.push(`/signup?username=${encodeURIComponent(username.value)}`)
}

// Always clickable: an empty field gets focus and a hint, a pending check
// claims as soon as it resolves, a taken or invalid name keeps focus on the field.
function claimUsername() {
  const state = !username.value ? 'empty' : isChecking.value ? 'checking' : isAvailable.value ? 'available' : 'unavailable'
  trackGoal('claim_click', { location: props.location, state })

  if (state === 'empty') {
    nudge.value = true
    inputEl.value?.focus()
    return
  }
  if (state === 'checking') {
    claimPending.value = true
    return
  }
  if (state === 'available') {
    goToSignup()
    return
  }
  inputEl.value?.focus()
}
</script>

<template>
  <div class="w-full max-w-xl">
    <!-- Input row -->
    <div class="flex items-center gap-2.5">
      <div class="flex flex-1 items-center rounded-xl border border-accented bg-elevated transition-colors focus-within:border-primary-400/60 shadow-(--surface-shadow)">
        <span class="select-none whitespace-nowrap pl-4 pr-1 text-base font-medium text-muted">
          mychar.gg/
        </span>
        <div class="relative flex-1">
          <label
            :for="`claim-username-${location}`"
            class="sr-only"
          >Username</label>
          <input
            :id="`claim-username-${location}`"
            ref="inputEl"
            :value="username"
            type="text"
            maxlength="20"
            class="w-full bg-transparent py-3.5 pr-4 text-base font-medium text-highlighted outline-none"
            :placeholder="' '"
            :aria-describedby="`username-status-${location}`"
            autocapitalize="none"
            autocomplete="off"
            spellcheck="false"
            @input="onInput(($event.target as HTMLInputElement).value)"
            @keydown.enter="claimUsername"
          >
          <!-- Animated placeholder -->
          <Transition
            enter-active-class="transition duration-300 ease-out"
            enter-from-class="opacity-0 translate-y-1"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition duration-200 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-1"
            mode="out-in"
          >
            <span
              v-if="!username"
              :key="currentPlaceholder"
              class="pointer-events-none absolute inset-0 flex items-center text-base font-medium text-dimmed"
            >
              {{ currentPlaceholder }}
            </span>
          </Transition>
        </div>
      </div>
      <UButton
        label="Claim"
        color="primary"
        variant="solid"
        size="xl"
        class="rounded-xl"
        :loading="claimPending"
        @click="claimUsername"
      />
    </div>

    <!-- Status indicator -->
    <div
      :id="`username-status-${location}`"
      class="mt-2.5 flex min-h-5 flex-wrap items-center gap-x-2 gap-y-1"
      :class="variant === 'compact' ? 'justify-center' : 'justify-start'"
      aria-live="polite"
    >
      <div
        v-if="nudge && !username"
        class="flex items-center gap-1.5 text-sm text-muted"
      >
        <UIcon
          name="i-lucide-at-sign"
          class="size-4 text-primary-400"
        />
        <span>Type the username you want first</span>
      </div>
      <div
        v-if="isChecking"
        class="flex items-center gap-1.5 text-sm text-muted"
      >
        <UIcon
          name="i-lucide-loader-2"
          class="size-4 animate-spin"
        />
        <span>Checking...</span>
      </div>
      <div
        v-else-if="validationError"
        class="flex items-center gap-1.5 text-sm text-error"
      >
        <UIcon
          name="i-lucide-x-circle"
          class="size-4"
        />
        <span>{{ validationError }}</span>
      </div>
      <div
        v-else-if="isAvailable === true"
        class="flex items-center gap-1.5 text-sm text-success"
      >
        <UIcon
          name="i-lucide-check-circle"
          class="size-4"
        />
        <span>mychar.gg/{{ username }} is available</span>
      </div>
      <div
        v-else-if="isAvailable === false"
        class="flex items-center gap-1.5 text-sm text-error"
      >
        <UIcon
          name="i-lucide-x-circle"
          class="size-4"
        />
        <span>Already taken</span>
        <template v-if="suggestions.length">
          <span class="text-muted">Try</span>
          <UButton
            v-for="name in suggestions"
            :key="name"
            :label="name"
            size="xs"
            color="neutral"
            variant="outline"
            @click="useSuggestion(name)"
          />
        </template>
      </div>
    </div>
  </div>
</template>
